// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/kernel/kernel.ts
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/fleet/kernel/src/index.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo enviar-share-app
//     node fleet/fleet.ts check --repo enviar-share-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
/**
 * The client plugin kernel.
 *
 * Four ideas, and nothing else:
 *   1. a service registry, so a feature reaches its dependencies without the
 *      app passing them down by hand;
 *   2. declared dependencies, so mount order is derived instead of guessed;
 *   3. capability gating, so a feature is SKIPPED (with a reason) where it
 *      cannot run, instead of crashing or being guarded app by app;
 *   4. reversible registration: everything a plugin registers is recorded under
 *      its name and undone on unmount, in reverse order.
 *
 * Point 4 is the whole reason this exists. Switching the active account is a
 * teardown, and a teardown that every app has to remember to write is a
 * teardown that 26 apps will eventually get wrong.
 *
 * SCOPE: the kernel gives ORDER, not ISOLATION. Every plugin runs with the
 * host's full authority. See infra/plugin-kernel/ADOPTION.md.
 */
import {
  CAPABILITIES,
  type Capability,
  type CapabilitySet,
  type PlatformTarget,
} from "./capabilities";

export * from "./capabilities";

/**
 * WHAT A PLUGIN CAN HAND THE APP'S UI.
 *
 * The kernel deliberately does not know what a navigation entry or a keyboard
 * command is: it only carries `TUi` values from the plugin that contributes
 * them to the host that renders them, with their type intact end to end.
 *
 * Each app declares its own union, e.g.
 *
 * ```ts
 * export type AppContribution =
 *   | { kind: "nav"; item: AppNavItem }
 *   | { kind: "command"; command: Command; binding?: KeyBinding };
 * ```
 *
 * and instantiates `new Kernel<AppContribution>()`. Adding a third kind of
 * contribution later is a line in that union — the kernel never changes.
 */
export type Contribution = unknown;

/**
 * A typed handle on a service.
 *
 * The registry is heterogeneous, so something has to carry the type from the
 * publisher to the consumer. A bare string cannot: a typo becomes a runtime
 * `undefined`. A token can — `serviceToken<T>("id")` is declared once, next to
 * the plugin that publishes it, and both sides import the same constant.
 *
 * `_type` is a phantom field: it never exists at runtime.
 */
export interface ServiceToken<T> {
  readonly id: string;
  readonly _type?: T;
}

export function serviceToken<T>(id: string): ServiceToken<T> {
  return { id };
}

export interface KernelEvent {
  seq: number;
  scope: string;
  plugin: string;
  event: string;
  data?: unknown;
}

export interface MountContext<TConfig = unknown, TUi = never> {
  readonly target: PlatformTarget;
  readonly caps: CapabilitySet;
  /** The account this mount belongs to. `null` before sign-in and under SSR. */
  readonly accountId: string | null;
  readonly config: TConfig;
  /** Throws if the service is missing — declare it in `requires` instead. */
  service<T>(token: ServiceToken<T>): T;
  tryService<T>(token: ServiceToken<T>): T | undefined;
  provide<T>(token: ServiceToken<T>, value: T): void;
  /**
   * Hands the host something to render. The kernel keeps contributions in
   * registration order and removes them on unmount, like every other effect.
   */
  contribute(item: TUi): void;
  /**
   * A reversible effect. Called on unmount, in reverse registration order.
   *
   * Async cleanups run concurrently and are NOT awaited serially: if you need
   * ordered teardown, group it into a single cleanup and chain it inside.
   */
  addCleanup(fn: () => void): void;
  log(event: string, data?: unknown): void;
}

export type MountPoint<TConfig, TUi = never> = (ctx: MountContext<TConfig, TUi>) => void;

export interface Plugin<TConfig = unknown, TUi = never> {
  /** Stable, unique, kebab-case. Used in logs and teardown provenance. */
  id: string;
  /** Services that must exist before this plugin mounts. */
  requires?: readonly ServiceToken<unknown>[];
  /** Services this plugin publishes. */
  provides?: readonly ServiceToken<unknown>[];
  /** Without these the plugin is skipped, with a reason. Never crashed. */
  capabilities?: readonly Capability[];
  /** Data layer: sources, subscriptions, services. Mounted first. */
  data?: MountPoint<TConfig, TUi>;
  /** UI surface: nav entries, view services. Mounted after `data`. */
  frontend?: MountPoint<TConfig, TUi>;
}

export interface PluginEntry<TConfig = unknown, TUi = never> {
  plugin: Plugin<TConfig, TUi>;
  config: TConfig;
  /**
   * The pages this plugin owns IN THIS APP, read without mounting anything.
   *
   * Every other contribution reaches the host through `ctx.contribute`, at
   * mount time. A route cannot: a router tree has to exist before any provider
   * does, so `contribute` would always run too late and the page would already
   * be reachable. Declaring the routes on the ENTRY — not on the plugin —
   * keeps the plugin product-agnostic while still binding the page's existence
   * to the one line that composes the feature.
   *
   * Deliberately NOT gated by capability or target: the route tree must be
   * identical in web, tauri and SSR or hydration breaks. What a missing
   * capability removes is the feature's SERVICE, and a page whose service is
   * absent renders its own empty state. What this removes is the page of a
   * feature the product does not have at all.
   */
  routes?: readonly unknown[];
}

/**
 * Heterogeneous list: every entry has already bound its own config type, so the
 * list cannot preserve it. `TUi` survives, which is what the host reads back.
 */
// oxlint-disable-next-line typescript/no-explicit-any
export type AnyPluginEntry<TUi = never> = PluginEntry<any, TUi>;

/** Binds a plugin to its configuration, type-checked at the call site. */
export function plugged<TConfig, TUi>(
  plugin: Plugin<TConfig, TUi>,
  config: TConfig,
  routes?: readonly unknown[],
): AnyPluginEntry<TUi> {
  return { plugin, config, routes };
}

/**
 * The pages a composition owns. Read at module load, before any provider.
 *
 * This is the ROUTE half of the same invariant `mount` enforces for services
 * and contributions: what a feature adds to the app exists only while the
 * feature is composed. Without it a product that never composed the plugin
 * still answered on the plugin's URL — with a page that could never fill,
 * because the data it reads was never part of that product.
 *
 * The caller names the route type; the kernel must not know TanStack exists.
 * Same erasure as `tryService`, and the same reason it is sound: the entry
 * that declared the routes is the entry that knows what they are.
 */
// oxlint-disable-next-line typescript/no-explicit-any
export function compositionRoutes<TRoute>(comp: Composition<any>): TRoute[] {
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  return comp.plugins.flatMap((entry) => (entry.routes ?? []) as readonly TRoute[]);
}

export interface Composition<TUi = never> {
  name: string;
  target: PlatformTarget;
  plugins: AnyPluginEntry<TUi>[];
}

/** An app is a composition: a name, a target and a list. Not wiring code. */
export function composition<TUi = never>(
  meta: { name: string; target: PlatformTarget },
  plugins: AnyPluginEntry<TUi>[],
): Composition<TUi> {
  return { ...meta, plugins };
}

export interface SkippedPlugin {
  id: string;
  reason: string;
}

export class KernelError extends Error {}

interface Cleanup {
  owner: string;
  fn: () => void;
}

export class MountedApp<TUi = never> {
  /** Everything the mounted plugins handed the UI, in registration order. */
  readonly contributions: TUi[] = [];
  readonly skipped: SkippedPlugin[] = [];
  readonly mounted: string[] = [];
  private readonly services = new Map<string, unknown>();
  private cleanups: Cleanup[] = [];
  private disposed = false;

  constructor(
    readonly mountedComposition: Composition<TUi>,
    readonly accountId: string | null,
    readonly log: KernelEvent[],
  ) {}

  service<T>(token: ServiceToken<T>): T {
    const found = this.tryService(token);
    if (found === undefined) {
      throw new KernelError(`service not provided: ${token.id}`);
    }
    return found;
  }

  tryService<T>(token: ServiceToken<T>): T | undefined {
    // The registry is heterogeneous by construction; the token's phantom type
    // is the contract that makes this narrowing sound. This is the ONE place
    // in the kernel where that erasure surfaces — see the guide, §"Servicios".
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    return this.services.get(token.id) as T | undefined;
  }

  get isDisposed() {
    return this.disposed;
  }

  /** @internal — called by `Kernel.mount`. */
  install(entry: AnyPluginEntry<TUi>, caps: CapabilitySet, seq: { n: number }) {
    // Explicitly typed rather than destructured: `AnyPluginEntry` erases the
    // config type to `any`, and letting that `any` spread through the mount
    // context is exactly the hole the token types are here to close.
    const plugin: Plugin<unknown, TUi> = entry.plugin;
    const config: unknown = entry.config;
    const own = (fn: () => void) => this.cleanups.push({ owner: plugin.id, fn });

    const ctx: MountContext<unknown, TUi> = {
      target: this.mountedComposition.target,
      caps,
      accountId: this.accountId,
      config,
      service: (token) => this.service(token),
      tryService: (token) => this.tryService(token),
      provide: (token, value) => {
        if (this.services.has(token.id)) {
          throw new KernelError(`service already provided: ${token.id} (by another plugin)`);
        }
        this.services.set(token.id, value);
        own(() => {
          this.services.delete(token.id);
        });
      },
      contribute: (item) => {
        this.contributions.push(item);
        own(() => {
          const i = this.contributions.indexOf(item);
          if (i !== -1) {
            this.contributions.splice(i, 1);
          }
        });
      },
      addCleanup: (fn) => {
        own(fn);
      },

      log: (event, data) => {
        this.log.push({
          seq: seq.n++,
          scope: this.accountId ?? "anonymous",
          plugin: plugin.id,
          event,
          data,
        });
      },
    };

    // `data` before `frontend`: a view may consume a service its own plugin
    // published a moment earlier. The kernel mounts whichever exist.
    plugin.data?.(ctx);
    plugin.frontend?.(ctx);
    this.mounted.push(plugin.id);
    ctx.log("mounted");
  }

  /**
   * Undoes every registered effect, newest first. Idempotent. A throwing
   * cleanup is logged and does not stop the rest — a half-torn-down app is
   * worse than a noisy one.
   */
  unmount() {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    for (let i = this.cleanups.length - 1; i >= 0; i--) {
      const cleanup: Cleanup | undefined = this.cleanups[i];
      if (!cleanup) {
        continue;
      }
      const { owner, fn } = cleanup;
      try {
        fn();
      } catch (error) {
        this.log.push({
          seq: -1,
          scope: this.accountId ?? "anonymous",
          plugin: owner,
          event: "cleanup_failed",
          data: String(error),
        });
      }
    }
    this.cleanups = [];
  }
}

export class Kernel<TUi = never> {
  readonly log: KernelEvent[] = [];
  private readonly seq = { n: 0 };

  /**
   * Mounts a composition for one account scope.
   *
   * Switching accounts is `unmount()` + `mount()` with the new scope. There is
   * no "refresh" path on purpose: a partial refresh is exactly how the previous
   * account's rows end up on the new account's first paint.
   */
  mount(
    comp: Composition<TUi>,
    opts: { accountId: string | null } = { accountId: null },
  ): MountedApp<TUi> {
    const caps = CAPABILITIES[comp.target];
    const app = new MountedApp<TUi>(comp, opts.accountId, this.log);
    const scope = opts.accountId ?? "anonymous";

    // 1. Capability gate. A plugin that cannot run here never gets to satisfy
    //    anyone's dependency either, so ordering below stays honest.
    const runnable: AnyPluginEntry<TUi>[] = [];
    for (const entry of comp.plugins) {
      const missing = (entry.plugin.capabilities ?? []).filter((c) => !caps[c]);
      if (missing.length === 0) {
        runnable.push(entry);
        continue;
      }
      const reason = `missing capability: ${missing.join(", ")}`;
      app.skipped.push({ id: entry.plugin.id, reason });
      this.log.push({
        seq: this.seq.n++,
        scope,
        plugin: entry.plugin.id,
        event: "skipped",
        data: missing,
      });
    }

    // 2. Dependency ordering. No topological sort library: repeated passes are
    //    O(n²) on a list that will never exceed a few dozen entries, and the
    //    error message it produces is worth more than the asymptotics.
    const provided = new Set<string>();
    let remaining = runnable;
    while (remaining.length > 0) {
      const ready = remaining.filter((e) =>
        (e.plugin.requires ?? []).every((r) => provided.has(r.id)),
      );
      if (ready.length === 0) {
        const blocked = remaining
          .map((e) => {
            const missing = (e.plugin.requires ?? [])
              .filter((r) => !provided.has(r.id))
              .map((r) => r.id);
            return `${e.plugin.id} needs [${missing.join(", ")}]`;
          })
          .join("; ");
        app.unmount();
        throw new KernelError(`unsatisfiable plugin dependencies: ${blocked}`);
      }
      for (const entry of ready) {
        app.install(entry, caps, this.seq);
        for (const token of entry.plugin.provides ?? []) {
          provided.add(token.id);
        }
      }
      remaining = remaining.filter((e) => !ready.includes(e));
    }

    return app;
  }
}

export * from "./context";
